import {useInfiniteQuery, useQuery, type InfiniteData} from "@tanstack/react-query";
import {
    DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
    stripDateRangeFilterLineItemsFromFilterConfiguration,
} from "~/components/ottopilotDataViewer/dataViewerDateRangeDefaults";
import {
    calendarDayToFilterIsoBounds,
    calendarVisibleRangeToFilterIsoBounds,
} from "~/utilities/calendarDateIso";
import {getAllCalendarDisplayMetricFilterColumnIds} from "~/domainModels/calendarDisplayMetrics/types";
import {type FilterConfiguration, type FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {
    CalendarDataSource,
    OttopilotDataViewerController,
} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import type {KanbanBoardColumnConfig} from "~/components/ottopilotDataViewer/view";
import type {CalendarViewMode} from "~/components/beatPlanningCalendar/calendarToolbarBus";
import {SortDirection} from "~/components/ottopilotDataViewer/sortConfiguration";
import type {Integer, Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {CALENDAR_MAX_VISIBLE_STACKED_EVENTS} from "~/components/beatPlanningCalendar/calendarEventCountDisplay";
import {
    dayCountsToYearStats,
    resolveCalendarRowsFetchKind,
    resolveCalendarRowsLimitForDataSource,
} from "~/components/ottopilotDataViewer/calendarFetchLimits";
import {
    resolveCalendarQueryMode,
    type CalendarOrchestrationEligibility,
} from "~/utilities/apis/eventContainerAndEventOrchestrationApi";

const DEFAULT_PAGE_SIZE = 50;

export type CalendarUnavailableReason = CalendarOrchestrationEligibility | "unsupported";

export function resolveCalendarQueryExecution(args: {
    controller: {
        calendarDataSource: CalendarDataSource;
        calendarOrchestrationEligibility: CalendarOrchestrationEligibility;
        calendarYearDayCountsQueryFn: OttopilotDataViewerController<{id: Uuid}>["calendarYearDayCountsQueryFn"];
    };
    fetchKind: ReturnType<typeof resolveCalendarRowsFetchKind>;
}): {
    canQuery: boolean;
    canQueryRows: boolean;
    canQueryYearCounts: boolean;
    unavailableReason: CalendarUnavailableReason | null;
} {
    const {controller} = args;
    if (controller.calendarDataSource === "orchestration") {
        const calendarQueryMode = resolveCalendarQueryMode({
            eligibility: controller.calendarOrchestrationEligibility,
            requiresOrchestration: true,
        });
        if (calendarQueryMode !== "orchestration") {
            return {
                canQuery: false,
                canQueryRows: false,
                canQueryYearCounts: false,
                unavailableReason: controller.calendarOrchestrationEligibility,
            };
        }
    }
    if (controller.calendarDataSource === "direct") {
        return {
            canQuery: true,
            canQueryRows: true,
            canQueryYearCounts: false,
            unavailableReason: null,
        };
    }
    if (args.fetchKind === "yearCounts" && controller.calendarYearDayCountsQueryFn === null) {
        return {
            canQuery: false,
            canQueryRows: false,
            canQueryYearCounts: false,
            unavailableReason: "unsupported",
        };
    }
    return {
        canQuery: true,
        canQueryRows: args.fetchKind !== "yearCounts",
        canQueryYearCounts: args.fetchKind === "yearCounts",
        unavailableReason: null,
    };
}

export function resolveCalendarRowRequestOptions(args: {
    activeDateColumnId: string;
    calendarDataSource: CalendarDataSource;
    calendarRowColumns: Array<string> | null;
    fetchKind: ReturnType<typeof resolveCalendarRowsFetchKind>;
    visibleRangeStart: Date | null;
    visibleRangeEnd: Date | null;
}): {
    columns: Array<string> | null;
    limit: number;
    maxRowsPerDay: number | null;
    maxRowsPerDayColumn: string | null;
} {
    const isDirectCalendar = args.calendarDataSource === "direct";
    const limit = resolveCalendarRowsLimitForDataSource({
        calendarDataSource: args.calendarDataSource,
        fetchKind: args.fetchKind,
        visibleRangeStart: args.visibleRangeStart,
        visibleRangeEnd: args.visibleRangeEnd,
    });
    const columns =
        isDirectCalendar === true
            ? null
            : buildCalendarProjectionColumns(args.calendarRowColumns, args.activeDateColumnId);
    const maxRowsPerDay =
        isDirectCalendar === false && args.fetchKind === "monthPreview"
            ? CALENDAR_MAX_VISIBLE_STACKED_EVENTS
            : null;
    return {
        columns,
        limit,
        maxRowsPerDay,
        maxRowsPerDayColumn: maxRowsPerDay === null ? null : args.activeDateColumnId,
    };
}

function getPageSize(pageSize: number): number {
    if (pageSize > 0) {
        return pageSize;
    }
    return DEFAULT_PAGE_SIZE;
}

function appendFilterLineItems(
    filterConfiguration: FilterConfiguration,
    lineItems: Array<FilterConfigurationLineItem>,
): FilterConfiguration {
    if (lineItems.length === 0) {
        return filterConfiguration;
    }
    return {
        revisionId: generateUuid(),
        lineItems: [...filterConfiguration.lineItems, ...lineItems],
    };
}

function halfOpenRangeFilterLineItems(
    columnId: string,
    range: {rangeStart: Iso8601DateTime; rangeEnd: Iso8601DateTime},
): Array<FilterConfigurationLineItem> {
    return [
        {
            columnId,
            operation: FilterOperation.lessThan,
            value: range.rangeEnd,
        },
        {
            columnId,
            operation: FilterOperation.greaterThanEqualTo,
            value: range.rangeStart,
        },
    ];
}

/**
 * Preserve the remaining active filters and AND the visible range on the
 * selected display metric; same-field bounds naturally intersect without
 * discarding one-sided or inclusive constraints.
 */
export function buildCalendarQueryFilterConfiguration(args: {
    filterConfiguration: FilterConfiguration;
    activeDateColumnId: string;
    visibleRange: {rangeStart: Iso8601DateTime; rangeEnd: Iso8601DateTime} | null;
}): FilterConfiguration {
    if (args.visibleRange === null) {
        return args.filterConfiguration;
    }
    return appendFilterLineItems(
        args.filterConfiguration,
        halfOpenRangeFilterLineItems(args.activeDateColumnId, args.visibleRange),
    );
}

export function buildLegacyDirectCalendarQueryFilterConfiguration(args: {
    dateRangeColumnId?: string;
    filterConfiguration: FilterConfiguration;
    calendarDateColumnIdsToStrip: Array<string>;
    startDateColumnId: string;
    endDateColumnId: string;
    visibleRange: {rangeStart: Iso8601DateTime; rangeEnd: Iso8601DateTime} | null;
}): FilterConfiguration {
    const baseFilterConfiguration = stripDateRangeFilterLineItemsFromFilterConfiguration(
        args.filterConfiguration,
        [args.dateRangeColumnId ?? DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID, ...args.calendarDateColumnIdsToStrip],
    );
    if (args.visibleRange === null) {
        return baseFilterConfiguration;
    }
    return appendFilterLineItems(baseFilterConfiguration, [
        {
            columnId: args.startDateColumnId,
            operation: FilterOperation.lessThan,
            value: args.visibleRange.rangeEnd,
        },
        {
            columnId: args.endDateColumnId,
            operation: FilterOperation.greaterThanEqualTo,
            value: args.visibleRange.rangeStart,
        },
    ]);
}

export function flattenCalendarSchedulePages<T extends {id: Uuid}>(pages: Array<Array<T>>): Array<T> {
    const seen = new Set<Uuid>();
    const rows: Array<T> = [];
    for (const page of pages) {
        for (const row of page) {
            if (seen.has(row.id)) {
                continue;
            }
            seen.add(row.id);
            rows.push(row);
        }
    }
    return rows;
}

export function getNextCalendarScheduleOffset({
    lastPageLength,
    pageSize,
    previousOffset,
}: {
    lastPageLength: number;
    pageSize: number;
    previousOffset: number;
}): number | undefined {
    return lastPageLength < pageSize ? undefined : previousOffset + lastPageLength;
}

export function buildCalendarProjectionColumns(
    configuredColumns: Array<string> | null,
    activeDateColumnId: string,
): Array<string> | null {
    if (configuredColumns === null) {
        return null;
    }
    const eventColumns = configuredColumns.filter((column) => !column.startsWith("eventContainer."));
    if (activeDateColumnId.startsWith("eventContainer.")) {
        return eventColumns;
    }
    return [...new Set([...eventColumns, activeDateColumnId])];
}

export function calendarMetricQueryIdentity(args: {
    activeDateColumnId: string;
    eligibility: string;
    metricId: string;
}): Array<string> {
    return [args.activeDateColumnId, args.metricId, args.eligibility];
}

/** Removes board-level kanban field filters so each column can apply its own bucket `in` filter. */
function stripKanbanColumnFilterLineItems(
    filterConfiguration: FilterConfiguration,
    filterFieldId: string,
): FilterConfiguration {
    const columnId = String(filterFieldId);
    const lineItems = filterConfiguration.lineItems.filter((lineItem) => lineItem.columnId !== columnId);
    if (lineItems.length === filterConfiguration.lineItems.length) {
        return filterConfiguration;
    }
    return {
        ...filterConfiguration,
        lineItems: lineItems,
    };
}

type OttopilotRowsQueryVariant = "table" | "list";

// Infinite-scroll table/list fetch (kept for reference; replaced by paginated useQuery below).
// function useOttopilotInfiniteRows<T extends {id: Uuid}>(
//     controller: OttopilotDataViewerController<T>,
//     variant: OttopilotRowsQueryVariant,
// ) {
//     const pageSize = getPageSize(controller.pageSize);
//
//     return useInfiniteQuery<Array<T>, Error, InfiniteData<Array<T>>, Array<unknown>, number>({
//         queryKey: [
//             "ottopilotDataViewer",
//             ...controller.queryKeyPrefix,
//             "rows",
//             variant,
//             controller.filterConfiguration.revisionId,
//             controller.sortConfiguration.revisionId,
//             controller.searchConfiguration.revisionId,
//             pageSize,
//         ],
//         initialPageParam: 0,
//         getNextPageParam: (lastPage, _pages, lastPageParam) => {
//             if (lastPage.length < pageSize) {
//                 return null;
//             }
//             return lastPageParam + lastPage.length;
//         },
//         queryFn: ({pageParam}) =>
//             controller.rowsQueryFn({
//                 filterConfiguration: controller.filterConfiguration,
//                 limit: pageSize,
//                 offset: pageParam,
//                 sortConfiguration: controller.sortConfiguration,
//                 searchConfiguration: controller.searchConfiguration,
//             }),
//         retry: false,
//         staleTime: Infinity,
//         refetchOnReconnect: false,
//         refetchOnWindowFocus: false,
//         refetchOnMount: true,
//     });
// }

function useOttopilotPaginatedRows<T extends {id: Uuid}>(
    controller: OttopilotDataViewerController<T>,
    variant: OttopilotRowsQueryVariant,
) {
    const pageSize = getPageSize(controller.pageSize);
    const pageNumber = controller.pageNumber > 0 ? controller.pageNumber : 1;
    const offset = (pageNumber - 1) * pageSize;

    return useQuery<Array<T>, Error>({
        queryKey: [
            "ottopilotDataViewer",
            ...controller.queryKeyPrefix,
            "rows",
            variant,
            controller.filterConfiguration.revisionId,
            controller.sortConfiguration.revisionId,
            controller.searchConfiguration.revisionId,
            pageSize,
            pageNumber,
        ],
        queryFn: () =>
            controller.rowsQueryFn({
                filterConfiguration: controller.filterConfiguration,
                limit: pageSize,
                offset: offset,
                sortConfiguration: controller.sortConfiguration,
                searchConfiguration: controller.searchConfiguration,
                columns: null,
                maxRowsPerDay: null,
                maxRowsPerDayColumn: null,
            }),
        retry: false,
        staleTime: Infinity,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });
}

export function useTableRows<T extends {id: Uuid}>(
    controller: OttopilotDataViewerController<T>,
) {
    return useOttopilotPaginatedRows(controller, "table");
}

/** Same fetch contract as {@link useTableRows}, but separate query key so list view is not coupled to table cache. */
export function useListViewRows<T extends {id: Uuid}>(
    controller: OttopilotDataViewerController<T>,
) {
    return useOttopilotPaginatedRows(controller, "list");
}

export function useKanbanColumnRowCount<T extends {id: Uuid}>({
    controller,
    column,
    filterFieldId,
    enabled = true,
}: {
    controller: OttopilotDataViewerController<T>;
    column: KanbanBoardColumnConfig;
    filterFieldId: Uuid | string;
    enabled?: boolean;
}) {
    const filterFieldIdString = String(filterFieldId);
    const dynamicFilterConfiguration = appendFilterLineItems(
        stripKanbanColumnFilterLineItems(controller.filterConfiguration, filterFieldIdString),
        [
            {
                columnId: filterFieldIdString,
                operation: FilterOperation.in,
                values: column.possibleValues.map((value) => String(value)),
            },
        ],
    );
    return useQuery<Integer, Error>({
        queryKey: [
            "ottopilotDataViewer",
            ...controller.queryKeyPrefix,
            "kanbanColumnRowCount",
            column.id,
            filterFieldId,
            controller.filterConfiguration.revisionId,
            controller.searchConfiguration.revisionId,
        ],
        queryFn: () =>
            controller.rowCountQueryFn({
                filterConfiguration: dynamicFilterConfiguration,
                searchConfiguration: controller.searchConfiguration,
            }),
        enabled: enabled,
        retry: false,
        staleTime: Infinity,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        placeholderData: (previousData) => previousData,
    });
}

export function useKanbanColumnRowCounts<T extends {id: Uuid}>(
    controller: OttopilotDataViewerController<T>,
) {
    const kanbanColumnRowCountsQueryFn = controller.kanbanColumnRowCountsQueryFn;
    return useQuery<Record<string, number>, Error>({
        queryKey: [
            "ottopilotDataViewer",
            ...controller.queryKeyPrefix,
            "kanbanColumnRowCounts",
            controller.filterConfiguration.revisionId,
            controller.sortConfiguration.revisionId,
            controller.searchConfiguration.revisionId,
        ],
        queryFn: () =>
            kanbanColumnRowCountsQueryFn!({
                filterConfiguration: controller.filterConfiguration,
                sortConfiguration: controller.sortConfiguration,
                searchConfiguration: controller.searchConfiguration,
            }),
        enabled: kanbanColumnRowCountsQueryFn !== undefined,
        retry: false,
        staleTime: Infinity,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        placeholderData: (previousData) => previousData,
    });
}

export function useKanbanColumnRows<T extends {id: Uuid}>({
    controller,
    column,
    filterFieldId,
}: {
    controller: OttopilotDataViewerController<T>;
    column: KanbanBoardColumnConfig;
    filterFieldId: Uuid | string;
}) {
    const pageSize = getPageSize(controller.pageSize);
    const filterFieldIdString = String(filterFieldId);
    const dynamicFilterConfiguration = appendFilterLineItems(
        stripKanbanColumnFilterLineItems(controller.filterConfiguration, filterFieldIdString),
        [
            {
                columnId: filterFieldIdString,
                operation: FilterOperation.in,
                values: column.possibleValues.map((value) => String(value)),
            },
        ],
    );
    const hasGlobalKanbanColumnTotalCounts = controller.kanbanColumnRowCountsQueryFn !== undefined;
    const globalKanbanColumnRowCountsQuery = useKanbanColumnRowCounts(controller);
    const perColumnCountQuery = useKanbanColumnRowCount({
        controller: controller,
        column: column,
        filterFieldId: filterFieldId,
        enabled: hasGlobalKanbanColumnTotalCounts === false,
    });
    const columnTotalCount =
        hasGlobalKanbanColumnTotalCounts === true
            ? globalKanbanColumnRowCountsQuery.data?.[String(column.id)]
            : perColumnCountQuery.data;

    return useInfiniteQuery<Array<T>, Error, InfiniteData<Array<T>>, Array<unknown>, number>({
        queryKey: [
            "ottopilotDataViewer",
            ...controller.queryKeyPrefix,
            "rows",
            "kanban",
            column.id,
            filterFieldId,
            controller.filterConfiguration.revisionId,
            controller.sortConfiguration.revisionId,
            controller.searchConfiguration.revisionId,
            pageSize,
        ],
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages, lastPageParam) => {
            const loadedCount = pages.reduce((sum, page) => sum + page.length, 0);
            const nextOffset = lastPageParam + pageSize;
            if (columnTotalCount !== undefined) {
                if (loadedCount >= columnTotalCount) {
                    return null;
                }
                if (lastPage.length === 0 && lastPageParam >= columnTotalCount) {
                    return null;
                }
                return nextOffset;
            }
            if (lastPage.length < pageSize) {
                return null;
            }
            return lastPageParam + lastPage.length;
        },
        queryFn: ({pageParam}) =>
            controller.rowsQueryFn({
                filterConfiguration: dynamicFilterConfiguration,
                limit: pageSize,
                offset: pageParam,
                sortConfiguration: controller.sortConfiguration,
                searchConfiguration: controller.searchConfiguration,
                columns: null,
                maxRowsPerDay: null,
                maxRowsPerDayColumn: null,
            }),
        retry: false,
        staleTime: Infinity,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        // Refetch only when stale (after invalidate); avoids refetch on every kanban pin/unpin remount.
        refetchOnMount: true,
    });
}

export function useCalendarRows<T extends {id: Uuid}>({
    controller,
    visibleRangeStart,
    visibleRangeEnd,
    calendarViewMode,
}: {
    controller: OttopilotDataViewerController<T>;
    visibleRangeStart: Date | null;
    visibleRangeEnd: Date | null;
    calendarViewMode: CalendarViewMode | null;
}) {
    const isDirectCalendar = controller.calendarDataSource === "direct";
    // Preview-3 compatibility: orchestration calendars query/bucket by Created At,
    // independently of the configured date used to place records on the calendar.
    const startDateColumnId = isDirectCalendar
        ? controller.activeCalendarDateColumnId
        : DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID;
    const endDateColumnId = startDateColumnId;
    const fetchKind = resolveCalendarRowsFetchKind(calendarViewMode);
    const isScheduleListView = fetchKind === "scheduleList";
    const yearCountsSupported =
        isDirectCalendar === false && controller.calendarYearDayCountsQueryFn !== null;
    const isYearCountsView = fetchKind === "yearCounts" && yearCountsSupported === true;
    const isDateRangeReady = visibleRangeStart !== null && visibleRangeEnd !== null;
    const shouldApplyDateRangeFilters = isScheduleListView === false && isDateRangeReady === true;
    const calendarQueryExecution = resolveCalendarQueryExecution({
        controller,
        fetchKind,
    });
    const isCalendarQueryEnabled = calendarQueryExecution.canQuery;

    const legacyStartDateColumnId = controller.dateRangeFieldIds?.startDateColumnId ?? "createdAt";
    const legacyEndDateColumnId = controller.dateRangeFieldIds?.endDateColumnId ?? "createdAt";
    const calendarDateColumnIdsToStrip =
        controller.calendarDisplayMetrics !== undefined
            ? getAllCalendarDisplayMetricFilterColumnIds(controller.calendarDisplayMetrics)
            : [
                  legacyStartDateColumnId,
                  ...(legacyEndDateColumnId !== legacyStartDateColumnId ? [legacyEndDateColumnId] : []),
              ];

    const calendarFilterRange =
        shouldApplyDateRangeFilters === true
            ? calendarVisibleRangeToFilterIsoBounds({
                  visibleRangeStart: visibleRangeStart,
                  visibleRangeEnd: visibleRangeEnd,
              })
            : null;
    const dynamicFilterConfiguration =
        isDirectCalendar === true
            ? buildLegacyDirectCalendarQueryFilterConfiguration({
                  dateRangeColumnId: controller.dateRangeColumnId,
                  filterConfiguration: controller.filterConfiguration,
                  calendarDateColumnIdsToStrip,
                  startDateColumnId,
                  endDateColumnId,
                  visibleRange: calendarFilterRange,
              })
            : buildCalendarQueryFilterConfiguration({
                  filterConfiguration: controller.filterConfiguration,
                  activeDateColumnId: startDateColumnId,
                  visibleRange: calendarFilterRange,
              });

    // Orchestration Year uses aggregate counts. Direct calendars retain their bounded
    // pre-orchestration row request until their APIs gain equivalent count endpoints.
    const effectiveFetchKindForRows = fetchKind;
    const {
        columns: calendarColumns,
        limit: rowLimit,
        maxRowsPerDay,
        maxRowsPerDayColumn,
    } = resolveCalendarRowRequestOptions({
        activeDateColumnId: startDateColumnId,
        calendarDataSource: controller.calendarDataSource,
        calendarRowColumns: controller.calendarRowColumns,
        fetchKind: effectiveFetchKindForRows,
        visibleRangeStart,
        visibleRangeEnd,
    });
    const stableCalendarSortConfiguration =
        (isDirectCalendar === true && effectiveFetchKindForRows !== "scheduleList") ||
        controller.sortConfiguration.lineItems.some((item) => item.columnId === "id")
            ? controller.sortConfiguration
            : {
                  ...controller.sortConfiguration,
                  lineItems: [
                      ...controller.sortConfiguration.lineItems,
                      {columnId: "id", direction: SortDirection.ascending},
                  ],
              };
    const metricQueryIdentity = calendarMetricQueryIdentity({
        activeDateColumnId: startDateColumnId,
        eligibility: controller.calendarOrchestrationEligibility,
        metricId: controller.calendarDisplayMetricId,
    });

    const yearCountsQuery = useQuery({
        queryKey: [
            "ottopilotDataViewer",
            ...controller.queryKeyPrefix,
            "rows",
            "calendar",
            "yearCounts",
            calendarFilterRange?.rangeStart ?? "no-start",
            calendarFilterRange?.rangeEnd ?? "no-end",
            controller.calendarDataSource,
            ...metricQueryIdentity,
            controller.filterConfiguration.revisionId,
            controller.searchConfiguration.revisionId,
        ],
        enabled:
            calendarQueryExecution.canQueryYearCounts === true &&
            isDateRangeReady === true &&
            controller.calendarYearDayCountsQueryFn !== null,
        queryFn: async () => {
            const queryFn = controller.calendarYearDayCountsQueryFn;
            if (queryFn === null) {
                return dayCountsToYearStats([]);
            }
            const dayCounts = await queryFn({
                filterConfiguration: dynamicFilterConfiguration,
                searchConfiguration: controller.searchConfiguration,
                dateColumnId: startDateColumnId,
            });
            return dayCountsToYearStats(dayCounts);
        },
        retry: false,
        staleTime: Infinity,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });

    const rowsQuery = useQuery<Array<T>, Error>({
        queryKey: [
            "ottopilotDataViewer",
            ...controller.queryKeyPrefix,
            "rows",
            "calendar",
            fetchKind,
            effectiveFetchKindForRows,
            calendarFilterRange?.rangeStart ?? "no-start",
            calendarFilterRange?.rangeEnd ?? "no-end",
            controller.calendarDataSource,
            ...metricQueryIdentity,
            endDateColumnId,
            controller.filterConfiguration.revisionId,
            controller.sortConfiguration.revisionId,
            controller.searchConfiguration.revisionId,
            rowLimit,
            maxRowsPerDay === null ? "no-per-day" : maxRowsPerDay,
            calendarColumns === null ? "all-columns" : calendarColumns.join(","),
        ],
        enabled:
            calendarQueryExecution.canQueryRows === true &&
            calendarViewMode !== null &&
            (fetchKind !== "yearCounts" || isDirectCalendar === true) &&
            fetchKind !== "scheduleList" &&
            (isScheduleListView === true || isDateRangeReady === true),
        queryFn: () =>
            controller.calendarRowsQueryFn({
                filterConfiguration: dynamicFilterConfiguration,
                limit: rowLimit,
                offset: 0,
                sortConfiguration: stableCalendarSortConfiguration,
                searchConfiguration: controller.searchConfiguration,
                columns: calendarColumns,
                maxRowsPerDay: maxRowsPerDay,
                maxRowsPerDayColumn: maxRowsPerDayColumn,
            }),
        retry: false,
        staleTime: Infinity,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });

    const scheduleQuery = useInfiniteQuery<Array<T>, Error, InfiniteData<Array<T>>, Array<unknown>, number>({
        queryKey: [
            "ottopilotDataViewer",
            ...controller.queryKeyPrefix,
            "rows",
            "calendar",
            "scheduleList",
            controller.calendarDataSource,
            ...metricQueryIdentity,
            controller.filterConfiguration.revisionId,
            controller.sortConfiguration.revisionId,
            controller.searchConfiguration.revisionId,
            rowLimit,
            calendarColumns === null ? "all-columns" : calendarColumns.join(","),
        ],
        initialPageParam: 0,
        getNextPageParam: (lastPage, _pages, lastPageParam) => getNextCalendarScheduleOffset({
            lastPageLength: lastPage.length,
            pageSize: rowLimit,
            previousOffset: lastPageParam,
        }),
        queryFn: ({pageParam}) =>
            controller.calendarRowsQueryFn({
                filterConfiguration: dynamicFilterConfiguration,
                limit: rowLimit,
                offset: pageParam,
                sortConfiguration: stableCalendarSortConfiguration,
                searchConfiguration: controller.searchConfiguration,
                columns: calendarColumns,
                maxRowsPerDay: null,
                maxRowsPerDayColumn: null,
            }),
        enabled:
            calendarQueryExecution.canQueryRows === true &&
            calendarViewMode !== null &&
            isScheduleListView === true,
        retry: false,
        staleTime: Infinity,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });

    const fetchDayEvents = async ({
        date,
        limit,
        offset,
    }: {
        date: Date;
        limit: number;
        offset: number;
    }): Promise<Array<T>> => {
        if (isCalendarQueryEnabled === false) {
            throw new Error(
                controller.calendarOrchestrationEligibility === "denied"
                    ? "Calendar read permission is required."
                    : "Calendar queries are not available for this workspace.",
            );
        }
        const dayRange = calendarDayToFilterIsoBounds(date);
        const dayFilterConfiguration =
            isDirectCalendar === true
                ? buildLegacyDirectCalendarQueryFilterConfiguration({
                      dateRangeColumnId: controller.dateRangeColumnId,
                      filterConfiguration: controller.filterConfiguration,
                      calendarDateColumnIdsToStrip,
                      startDateColumnId,
                      endDateColumnId,
                      visibleRange: dayRange,
                  })
                : buildCalendarQueryFilterConfiguration({
                      filterConfiguration: controller.filterConfiguration,
                      activeDateColumnId: startDateColumnId,
                      visibleRange: dayRange,
                  });
        return await controller.calendarRowsQueryFn({
            filterConfiguration: dayFilterConfiguration,
            limit: limit,
            offset: offset,
            sortConfiguration: stableCalendarSortConfiguration,
            searchConfiguration: controller.searchConfiguration,
            columns: calendarColumns,
            maxRowsPerDay: null,
            maxRowsPerDayColumn: null,
        });
    };

    if (isYearCountsView === true) {
        return {
            data: [] as Array<T>,
            yearStats: yearCountsQuery.data,
            isPending: yearCountsQuery.isPending,
            isFetching: yearCountsQuery.isFetching,
            error: yearCountsQuery.error,
            isYearCountsView: true as const,
            fetchDayEvents: fetchDayEvents,
            unavailableReason: calendarQueryExecution.unavailableReason,
            hasNextPage: false,
            isFetchingNextPage: false,
            fetchNextPage: async () => {},
            retry: async () => {
                await yearCountsQuery.refetch();
            },
        };
    }

    if (isScheduleListView === true) {
        return {
            data: scheduleQuery.data === undefined ? undefined : flattenCalendarSchedulePages(scheduleQuery.data.pages),
            yearStats: undefined,
            isPending: scheduleQuery.isPending,
            isFetching: scheduleQuery.isFetching,
            error: scheduleQuery.error,
            isYearCountsView: false as const,
            fetchDayEvents: fetchDayEvents,
            unavailableReason: calendarQueryExecution.unavailableReason,
            hasNextPage: scheduleQuery.hasNextPage === true,
            isFetchingNextPage: scheduleQuery.isFetchingNextPage,
            fetchNextPage: async () => {
                await scheduleQuery.fetchNextPage();
            },
            retry: async () => {
                if (scheduleQuery.data === undefined) {
                    await scheduleQuery.refetch();
                    return;
                }
                await scheduleQuery.fetchNextPage();
            },
        };
    }

    return {
        data: rowsQuery.data,
        yearStats: undefined,
        isPending: rowsQuery.isPending,
        isFetching: rowsQuery.isFetching,
        error: rowsQuery.error,
        isYearCountsView: false as const,
        fetchDayEvents: fetchDayEvents,
        unavailableReason: calendarQueryExecution.unavailableReason,
        hasNextPage: false,
        isFetchingNextPage: false,
        fetchNextPage: async () => {},
        retry: async () => {
            await rowsQuery.refetch();
        },
    };
}
